import type { PermissionState } from './index.types';
import { IS_ELECTRON } from 'Constants/env';
import { deviceLogger as logger } from 'Modules/softphone/utils/logger';
export type { PermissionState } from './index.types';

/**
 * Handles audio device discovery and permissions via MediaDevices API.
 * Enumerates microphones/speakers and listens for device changes (plug/unplug).
 */
export class DeviceService {
  private static instance: DeviceService;

  private constructor() {
    this.setupDeviceChangeListener();
  }

  static getInstance(): DeviceService {
    if (!DeviceService.instance) {
      DeviceService.instance = new DeviceService();
    }
    return DeviceService.instance;
  }

  /**
   * Setup listener for device changes (plug/unplug)
   */
  private setupDeviceChangeListener(): void {
    if (!navigator.mediaDevices) {
      logger.warn('MediaDevices API not available');
      return;
    }

    navigator.mediaDevices.addEventListener('devicechange', () => {
      logger.debug('Device change detected');
      void this.enumerateDevices();
    });
  }

  /**
   * Read the microphone permission via the Permissions API, which does not
   * activate the device and so never raises the browser recording indicator.
   * Electron reports 'granted': it exposes no microphone permission and access
   * is handled at the app/OS layer.
   */
  async getPermissionState(): Promise<PermissionState> {
    if (IS_ELECTRON) {
      return 'granted';
    }

    if (!navigator.permissions?.query) {
      logger.debug('Permissions API not available, assuming prompt');
      return 'prompt';
    }

    try {
      const status = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      logger.debug(`Microphone permission state: ${status.state}`);
      return status.state as PermissionState;
    } catch {
      logger.warn('Failed to query microphone permission, assuming prompt');
      return 'prompt';
    }
  }

  /**
   * Request microphone permissions.
   *
   * Opens a stream, so it prompts when undecided and raises the browser
   * recording indicator. Only call from a user action that needs the mic.
   */
  async requestPermissions(): Promise<PermissionState> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      logger.info('Microphone permission granted');
      return 'granted';
    } catch (error) {
      logger.error(
        'Microphone permission denied',
        error instanceof Error ? error : new Error(String(error))
      );
      return 'denied';
    }
  }

  /**
   * Enumerate all media devices
   */
  async enumerateDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      logger.debug(`Found ${devices.length} devices`);
      return devices;
    } catch (error) {
      logger.error(
        'Failed to enumerate devices',
        error instanceof Error ? error : new Error(String(error))
      );
      return [];
    }
  }

  /**
   * Get audio input devices (microphones). Entries with an empty deviceId are
   * dropped: until a stream has been opened once the browser only returns
   * placeholders, which cannot be selected or persisted.
   */
  async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    const devices = await this.enumerateDevices();
    return devices.filter(
      (device) => device.kind === 'audioinput' && !!device.deviceId
    );
  }

  /**
   * Get audio output devices (speakers). Placeholder entries are dropped, see
   * getAudioInputDevices().
   */
  async getAudioOutputDevices(): Promise<MediaDeviceInfo[]> {
    const devices = await this.enumerateDevices();
    return devices.filter(
      (device) => device.kind === 'audiooutput' && !!device.deviceId
    );
  }

  /**
   * Check if a device is still available
   */
  async isDeviceAvailable(deviceId: string): Promise<boolean> {
    const devices = await this.enumerateDevices();
    return devices.some((device) => device.deviceId === deviceId);
  }
}
